import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { IconPlus, IconX } from '@tabler/icons-react';

interface PromoCodeInputProps {
  eventCode: string;
  eventData?: { code: string; description?: string };
  /** When false, event tag is hidden (code still sent to backend). */
  eventAppliesToPlan?: boolean;
  referralCode: string;
  /** When false, referral tag is hidden (code still sent to backend). */
  referralValid?: boolean;
  referralCodeInput: string;
  promoError: string;
  showPromoInput: boolean;
  onExpandPromoInput: () => void;
  planPricing: any;
  currentPlan: any;
  onReferralCodeChange: (value: string) => void;
  onReferralCodeBlur: () => void;
  onReferralCodeKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onRemoveReferralCode: () => void;
}

export const PromoCodeInput = ({
  eventCode,
  eventData,
  eventAppliesToPlan = true,
  referralCode,
  referralValid = true,
  referralCodeInput,
  promoError,
  showPromoInput,
  onExpandPromoInput,
  planPricing,
  currentPlan,
  onReferralCodeChange,
  onReferralCodeBlur,
  onReferralCodeKeyDown,
  onRemoveReferralCode
}: PromoCodeInputProps) => {
  // @ts-ignore - Type instantiation depth limitation in TypeScript
  const { t } = useTranslation();
  const eventDisplayName = eventCode && eventAppliesToPlan
    ? (eventData?.description?.trim() || eventCode)
    : t('promo.WEBSITE.name');
  const referralApplied = referralCode && !promoError && referralValid;

  return (
    <div className="w-full mb-4">
      <div className="flex items-center gap-0 mb-2">
        <label htmlFor="promo-code" className="text-sm font-medium text-slate-700 h-5">
          {/* @ts-ignore */}
          {t('common.promoCode')}
        </label>
        {!referralApplied && !showPromoInput && !promoError && (
          <button
            type="button"
            onClick={onExpandPromoInput}
            className="relative h-5"
            aria-label="Add promo code"
          >
            <div className="absolute -top-4 -left-4 pl-5 pr-4 pt-4 pb-2 text-slate-900 group">
              <div className="rounded-full p-1 group-hover:bg-slate-100 transition-all duration-300">
                <IconPlus className="w-3 h-3" />
              </div>
            </div>
          </button>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {referralApplied ? (
          <div className="flex items-center gap-1.5 bg-green-100 text-green-600 px-3 py-2 rounded-md text-sm border border-green-200">
            <span className="font-medium">{referralCode}</span>
            <button
              onClick={onRemoveReferralCode}
              className="rounded p-0.5"
              aria-label="Remove referral code"
            >
              <IconX className="w-3 h-3" />
            </button>
          </div>
        ) : (showPromoInput || promoError) ? (
          <div className="w-full">
            <Input
              id="promo-code"
              type="text"
              value={referralCodeInput}
              onChange={(e) => onReferralCodeChange(e.target.value)}
              onBlur={onReferralCodeBlur}
              onKeyDown={onReferralCodeKeyDown}
              placeholder={t('common.referralCode')}
              className={promoError ? 'border-red-300 text-base placeholder:text-slate-400' : 'text-base placeholder:text-slate-400'}
            />
            {promoError && (
              <div className="text-xs text-red-500 mt-1">{promoError}</div>
            )}
          </div>
        ) : null}
        <div className="flex items-center gap-1.5 bg-sky-100 text-sky-700 px-3 py-2 rounded-md text-sm border border-sky-200">
          <span className="font-medium">{t('common.eventCodeApplied')}: {eventDisplayName}</span>
        </div>
      </div>
      {referralApplied && planPricing?.referralSavings != null && planPricing.referralSavings > 0 && (
        <div
          className="text-xs text-green-600 mt-1.5"
          dangerouslySetInnerHTML={{ // @ts-ignore
            __html: t('common.referralCodeAppliedNarrow', {
              code: referralCode,
              currency: currentPlan?.pricing.currency,
              amount: Number(planPricing.referralSavings).toLocaleString()
            })
          }}
        />
      )}
    </div>
  );
};
